import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { scrollSliderToNeighbor } from "../../utils/sliderNavigation";

const iconPaths = [
  <path
    key="advisor"
    d="M12 5.25C13.7949 5.25 15.25 6.70507 15.25 8.5C15.25 10.2949 13.7949 11.75 12 11.75C10.2051 11.75 8.75 10.2949 8.75 8.5C8.75 6.70507 10.2051 5.25 12 5.25ZM12 13.25C15.0376 13.25 17.5 14.8735 17.5 16.875V18H6.5V16.875C6.5 14.8735 8.96243 13.25 12 13.25Z"
    fill="currentColor"
  />,
  <path
    key="consultant"
    d="M5 6.5H14.5V14.5H5V6.5ZM14.5 6.5L18.5 4.5V16.5L14.5 14.5V6.5ZM7.25 8.75H12.25M7.25 11.25H10.75"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  />,
  <path
    key="admission"
    d="M8 4.75H16L19 7.75V19.25H8V4.75ZM16 4.75V7.75H19M10.5 11H16.5M10.5 14H16.5M10.5 17H14"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  />,
  <path
    key="residency"
    d="M12 20C15.6 16.1 18 13.2 18 10.25C18 6.79822 15.3137 4 12 4C8.68629 4 6 6.79822 6 10.25C6 13.2 8.4 16.1 12 20ZM12 12.5C13.2426 12.5 14.25 11.4926 14.25 10.25C14.25 9.00736 13.2426 8 12 8C10.7574 8 9.75 9.00736 9.75 10.25C9.75 11.4926 10.7574 12.5 12 12.5Z"
    fill="currentColor"
  />,
  <path
    key="transport"
    d="M4.5 8.5H15.5L18.5 11.5V16.5H17C17 17.6046 16.1046 18.5 15 18.5C13.8954 18.5 13 17.6046 13 16.5H9C9 17.6046 8.10457 18.5 7 18.5C5.89543 18.5 5 17.6046 5 16.5H4.5V8.5ZM7 17C7.27614 17 7.5 16.7761 7.5 16.5C7.5 16.2239 7.27614 16 7 16C6.72386 16 6.5 16.2239 6.5 16.5C6.5 16.7761 6.72386 17 7 17ZM15 17C15.2761 17 15.5 16.7761 15.5 16.5C15.5 16.2239 15.2761 16 15 16C14.7239 16 14.5 16.2239 14.5 16.5C14.5 16.7761 14.7239 17 15 17ZM15.5 10V13H17"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  />,
  <path
    key="support"
    d="M12 5C8.68629 5 6 7.68629 6 11V12.75C6 13.4404 5.44036 14 4.75 14C4.33579 14 4 14.3358 4 14.75C4 15.1642 4.33579 15.5 4.75 15.5H6.25C7.2165 15.5 8 14.7165 8 13.75V11C8 8.79086 9.79086 7 12 7C14.2091 7 16 8.79086 16 11V13.75C16 14.7165 16.7835 15.5 17.75 15.5H19.25C19.6642 15.5 20 15.1642 20 14.75C20 14.3358 19.6642 14 19.25 14C18.5596 14 18 13.4404 18 12.75V11C18 7.68629 15.3137 5 12 5ZM9.5 18H14.5"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  />,
] as const;

const ServiceIcon: React.FC<{ index: number }> = ({ index }) => (
  <div className="flex h-10 w-10 items-center justify-center bg-main-color">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="text-white"
    >
      {iconPaths[index % iconPaths.length]}
    </svg>
  </div>
);

const ServicesList: React.FC = () => {
  const { t, dir } = useLanguage();
  const isRtl = dir === "rtl";
  const content = (t as any)?.services || {};
  const items = content.items || [];
  const sectionRef = useRef<HTMLElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const scrollCards = (direction: "previous" | "next") => {

  scrollSliderToNeighbor({

    scroller: scrollerRef.current,

    direction,

    isRtl,

  });

};

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -18% 0px", threshold: 0.2 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-white py-16 sm:py-20 lg:py-28"
      aria-labelledby="services-list-title"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className={`site-container flex flex-col gap-8 ${isRtl ? "items-end" : "items-start"}`}>
        <div
          className={`flex w-full items-end justify-between gap-6 ${
            isRtl ? "md:flex-row-reverse" : ""
          }`}
        >
          <div
            className={`flex max-w-[42rem] flex-col justify-center transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            } ${isRtl ? "items-end text-right" : "items-start text-left"}`}
          >
            <h2
              id="services-list-title"
              className="font-sans text-2xl font-semibold capitalize leading-tight text-teal-900 sm:text-[32px] sm:leading-[1.2]"
            >
              {content.title || "Everything you need. Nothing left out."}
            </h2>
          </div>

          <div
            className={`hidden items-center gap-3 transition-all duration-700 ease-out md:flex ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            } ${isRtl ? "flex-row-reverse" : ""}`}
          >
            <button
              type="button"
              onClick={() => scrollCards("previous")}
              aria-label="Previous services"
              className="flex h-10 w-10 items-center justify-center border border-second-color text-second-color transition-colors hover:bg-second-color hover:text-main-color focus:outline-none focus:ring-2 focus:ring-main-color"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d={isRtl ? "M7.5 4L13.5 10L7.5 16" : "M12.5 4L6.5 10L12.5 16"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => scrollCards("next")}
              aria-label="Next services"
              className="flex h-10 w-10 items-center justify-center bg-second-color text-main-color transition-colors hover:bg-main-color hover:text-second-color focus:outline-none focus:ring-2 focus:ring-main-color"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d={isRtl ? "M12.5 4L6.5 10L12.5 16" : "M7.5 4L13.5 10L7.5 16"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="w-full snap-x snap-mandatory overflow-x-auto pb-2 [scrollbar-width:none]"
        >
          <div
            className={`grid min-w-max grid-flow-col gap-6 ${
              isRtl ? "[direction:rtl] text-right" : "[direction:ltr]"
            }`}
          >
            {items.map((item: { title: string; description: string }, index: number) => (
              <article
                key={item.title}
                className={`flex h-96 w-[84vw] max-w-80 shrink-0 snap-start flex-col justify-between overflow-hidden bg-stone-100 p-6 transition-all duration-700 ease-out sm:w-[22rem] lg:w-80 ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
                } ${isRtl ? "text-right" : "text-left"}`}
                style={{ transitionDelay: `${index * 90}ms` }}
              >
                <div className={`${isRtl ? "self-end" : "self-start"}`}>
                  <ServiceIcon index={index} />
                </div>

                <div className={`flex max-w-[16rem] flex-col gap-1 ${isRtl ? "items-end text-right" : "items-start text-left"}`}>
                  <h3 className="font-sans text-xl font-semibold leading-6 text-teal-900">
                    {item.title}
                  </h3>
                  <p className="font-sans text-sm font-normal leading-6 text-teal-900 sm:text-base">
                    {item.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesList;
